import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Spinner } from "@graffer-inc/console-design-kit";

import { reportUnexpectedError } from "../../../../error-reporter";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../graphql/client";
import { useServiceAccountsListPage } from "../../../../hooks/service-account/service-accounts-list";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import { ServiceAccountsListController } from "../../../organisms/service-account/service-accounts-list-controller";
import {
  ServiceAccountsListTable,
  ServiceAccountWithDetailPageLink,
} from "../../../organisms/service-account/service-accounts-list-table";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderR } from "../routes";

export const ServiceAccountsPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { serviceAccounts } = useServiceAccountsListPage(tenantId);

  const history = useHistory();

  const handleClickCreate = React.useCallback(() => {
    history.push(
      ServiceProviderR.ServiceAccountCreate.link({
        tenantId,
      }),
    );
  }, [history, tenantId]);

  const pageState = useServiceAccountOrgProduct(tenantId);

  const serviceAccountsWithDetailLink = React.useMemo(
    () =>
      serviceAccounts.map((serviceAccount) => {
        const detailPageLink = ServiceProviderR.ServiceAccountDetail.link({
          tenantId,
          serviceAccountId: serviceAccount.id,
        });

        return {
          ...serviceAccount,
          detailPageLink,
        };
      }) as ServiceAccountWithDetailPageLink[],
    [serviceAccounts, tenantId],
  );

  return (
    <ServiceProviderTemplate
      breadcrumbs={[
        {
          to: ServiceProviderR.Home.link({}),
          title: "Home",
        },
      ]}
    >
      <div className="has-text-left">
        <div className="columns is-centered">
          <div className="column">
            <ServiceProviderTabs orgId={tenantId} />
            {pageState === "loading" && (
              <div className="content">
                <Spinner loading />
              </div>
            )}
            {pageState === "serviceAccountEnabled" && (
              <>
                <ServiceAccountsListController
                  onClickCreate={handleClickCreate}
                />
                <ServiceAccountsListTable
                  serviceAccounts={serviceAccountsWithDetailLink}
                />
              </>
            )}
            {pageState === "serviceAccountDisabled" && (
              <div className="content">
                <p>
                  「製品設定」画面から、サービスアカウント機能を有効化してください。
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ServiceProviderTemplate>
  );
};

type PageState = "loading" | "serviceAccountDisabled" | "serviceAccountEnabled";

const useServiceAccountOrgProduct = (tenantId: string): PageState => {
  const [pageState, setPageState] = React.useState<PageState>("loading");

  React.useEffect(() => {
    const fetchOrgProduct = async () => {
      try {
        const res = await runGqlQueryV2(() =>
          gqlClient.allOrgProductsByOrgId({ orgId: tenantId }, gqlHeader("q")),
        );
        if (res.error) {
          toast.error("製品情報の取得に失敗しました。");
          return;
        }

        setPageState(
          res.data?.allOrgProductsByOrgId.some((p) => {
            return p.productCode === "ServiceAccount" && p.plan !== "NotSet";
          })
            ? "serviceAccountEnabled"
            : "serviceAccountDisabled",
        );
      } catch (e) {
        reportUnexpectedError(e);
        setPageState("serviceAccountDisabled");
      }
    };

    fetchOrgProduct();
  }, [tenantId]);

  return pageState;
};
